import { integrationConfig } from '@aibees/elephant/constants/integration';
import { useTableActionContext } from '@aibees/elephant/context';
import { useAddIntegrationMutationApi } from '@aibees/elephant/hooks/integration/use-add-integration-mutation-api';
import { useUpdateIntegrationMutationApi } from '@aibees/elephant/hooks/integration/use-update-integration-mutation-api';
import { notificationIconMap } from '@aibees/elephant/utils';
import { DialogTitle, OutlinedInput } from '@aibees/shared/components';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  Typography,
} from '@mui/material';
import { memo, useMemo, useState } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { IntegrationConnectDialogProps as Props } from './types';

export const IntegrationConnectDialog = memo(
  ({ className, state, isEdit, item, onClose }: Props) => {
    const notify = useNotify();
    const [name, setName] = useState(isEdit ? item?.name : '');
    const [password, setPassword] = useState('');
    const [hasExistingPassword, setHasExistingPassword] = useState(isEdit);
    const addApi = useAddIntegrationMutationApi();
    const editApi = useUpdateIntegrationMutationApi(item?.id);
    const api = isEdit ? editApi : addApi;
    const disabled =
      !name ||
      (!hasExistingPassword && !password) ||
      (hasExistingPassword && name === item?.name) ||
      api.isLoading;

    const {
      integrationConnectDialogState,
      integrationConfig: integrationConfigContext,
    } = useTableActionContext();

    const buttonText = useMemo(
      () => (isEdit ? 'Save' : 'Add API Key'),
      [isEdit]
    );

    const integrationName = useMemo(
      () =>
        integrationConfig.find((config) => config.type === item?.type)?.name ||
        integrationConfigContext?.name,
      [item?.type, integrationConfigContext?.name]
    );

    const handlePaste = async () => {
      try {
        const clipboardText = await navigator.clipboard.readText();
        setPassword(clipboardText);
        setHasExistingPassword(false);
      } catch (err) {
        console.error('Paste failed:', err);
        alert('Clipboard access is blocked or unsupported.');
      }
    };

    const handleClose = () => {
      integrationConnectDialogState.onFalse();
      state?.onFalse();
      onClose?.();

      if (!isEdit) {
        setName('');
        setPassword('');
        setHasExistingPassword(false);
      }
    };

    const handleSubmit = () => {
      const token = hasExistingPassword ? null : password;
      const key = Date.now().toString();
      api
        .mutateAsync({
          name,
          type: item?.type ?? integrationConfigContext?.type,
          config: {
            token: item?.config?.token ?? token,
          },
        })
        .then((response) => {
          notify.complete(
            'Integration Connected',
            'Your app integration has been added successfully.',
            key,
            {
              icon: notificationIconMap.Integration,
            }
          );
          setName('');
          setPassword('');
          setHasExistingPassword(false);
          handleClose();
        });
    };

    return (
      <Styled.Wrapper
        className={classnames('IntegrationConnectDialog', className)}
        sx={SX.root}
      >
        <Dialog
          open={state?.value ?? integrationConnectDialogState.value}
          fullWidth={true}
          maxWidth="xs"
          TransitionComponent={Fade}
          onClose={handleClose}
        >
          <DialogTitle
            title={`Connect ${integrationName} to your Account`}
            icon="power"
            onClose={handleClose}
          />

          <DialogContent sx={SX.dialogContent}>
            <Typography variant="body2" fontWeight={700} color="grey.700">
              Enter your {integrationName} API Key
            </Typography>

            <OutlinedInput
              label="Name Your Connection"
              placeholder="Enter Name Your Connection"
              size="small"
              defaultValue={item?.name}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <OutlinedInput
              label={`${integrationName} API Key`}
              placeholder={
                hasExistingPassword ? 'Existing API Key' : 'Enter your API Key'
              }
              size="small"
              endIcon="paste-outline"
              type="password"
              value={
                hasExistingPassword
                  ? '••••••••••••••••••••••••••••••••••••••••'
                  : password
              }
              onChange={(e) => {
                setPassword(e.target.value);
                setHasExistingPassword(false);
              }}
              onClickEndIcon={handlePaste}
            />
          </DialogContent>

          <DialogActions>
            <Button size="small" color="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={disabled}
              onClick={handleSubmit}
            >
              {buttonText}
            </Button>
          </DialogActions>
        </Dialog>
      </Styled.Wrapper>
    );
  }
);

IntegrationConnectDialog.displayName = 'IntegrationConnectDialog';
